import { FullScreenOverlay } from '@components'

import { Color, IconButton, Text } from '@billy/design-system'
import React, { ReactElement, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { BackToSchoolCampaign } from '@features/backToSchoolCampaign/elements/BackToSchoolCampaign'
import { useBackToSchoolCampaign } from '@features/backToSchoolCampaign/hooks/useBackToSchoolCampaign'
import { SubscriptionPeriod } from '@modules-deprecated/app/organization/enums/subscriptionPeriod'
import { SubscriptionPlan } from '@modules-deprecated/app/organization/enums/subscriptionPlan'

import { useSegment } from '../../../../../../hooks'
import { SettingsRoute } from '../../../../enums/settingsRoute'
import { useOrganizationSubscriptionSettings } from '../../contexts/organizationSubscriptionSettingsContext'
import { SubscriptionPlans } from '../SubscriptionPlans'
import * as Styled from './styles'

interface SubscriptionPlansOverlayProps {
  onPlanSelect: (plan: SubscriptionPlan, period?: SubscriptionPeriod) => void
}

export const SubscriptionPlansOverlay = ({ onPlanSelect }: SubscriptionPlansOverlayProps): ReactElement | null => {
  const { t } = useTranslation()
  const { track } = useSegment()
  const navigate = useNavigate()
  const { audience, isLoading: isCampaignLoading, remainingCampaignDays } = useBackToSchoolCampaign()
  const { onEditSubscription } = useOrganizationSubscriptionSettings()
  const [shouldShowCampaign, setShouldShowCampaign] = useState(true)

  const handleCloseButtonClick = useCallback(() => {
    if (audience && !shouldShowCampaign) {
      setShouldShowCampaign(true)
      return
    }

    navigate(SettingsRoute.OrganizationSubscription)
  }, [audience, navigate, shouldShowCampaign])

  useEffect(() => {
    track('xxx - iam - pricing viewed')
  }, [track])

  if (isCampaignLoading) {
    return null
  }

  if (audience && shouldShowCampaign) {
    return (
      <Styled.CampaignOverlay>
        <BackToSchoolCampaign
          audience={audience}
          onClose={handleCloseButtonClick}
          onEditSubscription={onEditSubscription}
          onViewMonthlyPlans={() => setShouldShowCampaign(false)}
          remainingCampaignDays={remainingCampaignDays}
        />
      </Styled.CampaignOverlay>
    )
  }

  return (
    <FullScreenOverlay bgColor={Color.Grey100}>
      <Styled.OverlayContainer>
        <Styled.OverlaySubscriptionPlansAndHeaderWrapper>
          <Styled.OverlayHeader>
            <Styled.OverlayHeaderLeftSection />
            <Text variant="h1">{t('organization_subscription.plans.choose_your_subscription')}</Text>
            <IconButton icon="x" onClick={handleCloseButtonClick} />
          </Styled.OverlayHeader>
          <SubscriptionPlans onPlanSelect={onPlanSelect} />
        </Styled.OverlaySubscriptionPlansAndHeaderWrapper>
      </Styled.OverlayContainer>
    </FullScreenOverlay>
  )
}
